import React, { useState, useEffect } from 'react';
import {
    getAllNotifications,
    deleteByUid,
    INotification,
} from '../../services/api/notificationService';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import NotificationCard from './NotificationCard';
import NotificationPreviewDialog from './NotificationPreviewDialog';

interface NotificationListProps {
    refreshTrigger: number;
}

const NotificationList: React.FC<NotificationListProps> = ({
    refreshTrigger,
}) => {
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deletingUid, setDeletingUid] = useState<string | null>(null);
    const [previewNotification, setPreviewNotification] =
        useState<INotification | null>(null);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAllNotifications();
            // Sort reverse chronological (newest first)
            const sorted = (data || []).sort(
                (a: INotification, b: INotification) =>
                    new Date(b.createdAt || 0).getTime() -
                    new Date(a.createdAt || 0).getTime()
            );
            setNotifications(sorted);
        } catch (err: any) {
            setError(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Failed to load notifications'
            );
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [refreshTrigger]);

    const handleDelete = async (uid: string) => {
        setDeletingUid(uid);
        try {
            await deleteByUid(uid);
            setNotifications((prev) =>
                prev.filter((n) => n._id !== uid && n.uid !== uid)
            );
        } finally {
            setDeletingUid(null);
        }
    };

    return (
        <Card className="border-white/10">
            <CardHeader>
                <CardTitle>Notification overview</CardTitle>
                <CardDescription>
                    Status counts per notification. Deleting removes it for all
                    recipients.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {error && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {loading ? (
                    <p className="text-sm text-muted-foreground">
                        Loading notifications…
                    </p>
                ) : notifications.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        No notifications yet. Create one above.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {notifications.map((notification) => (
                            <li key={notification._id || notification.uid}>
                                <NotificationCard
                                    variant="admin"
                                    notification={notification}
                                    onPreview={setPreviewNotification}
                                    onDelete={() =>
                                        handleDelete(notification._id!)
                                    }
                                    isDeleting={
                                        deletingUid === notification._id
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                )}

                {!loading && notifications.length > 0 && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={fetchNotifications}
                    >
                        Refresh list
                    </Button>
                )}
            </CardContent>

            <NotificationPreviewDialog
                notification={previewNotification}
                open={!!previewNotification}
                onOpenChange={(open) => {
                    if (!open) setPreviewNotification(null);
                }}
            />
        </Card>
    );
};

export default NotificationList;
